import { useState } from "react";
import { Box, Cylinder, Minus, Package, Plus, Trash2, Warehouse } from "lucide-react";
import { Step, Q_INPUT, Q_LABEL } from "./Step";
import { fmtMoney, newKey } from "../../lib/format";

const TYPES = [
  { type: "barrel_55", label: "55 Gal Barrel", icon: Cylinder, fixed: true },
  { type: "barrel_77", label: "77 Gal Jumbo Barrel", icon: Cylinder, fixed: true },
  { type: "box", label: "Box / Other Item", sub: "bins, furniture, appliances", icon: Box },
  { type: "pallet", label: "Pallet / Crate", icon: Warehouse },
];
const LABELS = Object.fromEntries(TYPES.map((t) => [t.type, t.label]));

function priceHint(t, rate, cur) {
  if (!rate) return "Select route";
  if (t.fixed) return fmtMoney(rate[t.type], cur);
  return `${fmtMoney(t.type === "box" ? rate.box_cuft : rate.pallet_cuft, cur)} / cu ft`;
}

function DimensionForm({ type, onAdd, onCancel }) {
  const [d, setD] = useState({ length: "", width: "", height: "", qty: "1", description: "" });
  const set = (k, v) => setD({ ...d, [k]: v });
  const add = () => {
    const n = (v) => parseFloat(v) || 0;
    if (!n(d.length) || !n(d.width) || !n(d.height)) return;
    onAdd({ key: newKey(), type, length: n(d.length), width: n(d.width), height: n(d.height), qty: Math.max(1, parseInt(d.qty, 10) || 1), description: d.description });
  };
  return (
    <div className="mt-4 p-5 bg-[#F1F5F9] border border-slate-200 rounded-sm" data-testid="quote-dimension-form">
      <div className="text-sm font-semibold text-[#1E2A78] mb-3">{LABELS[type]} — dimensions in inches</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {["length", "width", "height", "qty"].map((k) => (
          <div key={k}>
            <label className={Q_LABEL}>{k === "qty" ? "Quantity" : `${k} (in)`}</label>
            <input type="number" min="0" step={k === "qty" ? "1" : "0.5"} value={d[k]} onChange={(e) => set(k, e.target.value)} className={Q_INPUT} data-testid={`quote-dim-${k}`} />
          </div>
        ))}
      </div>
      <input placeholder="What is it? (e.g. 50&quot; TV, sofa, tote of clothes)" value={d.description} onChange={(e) => set("description", e.target.value)} className={`${Q_INPUT} mt-3`} data-testid="quote-dim-description" />
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={add} className="px-4 py-2 bg-[#1E2A78] text-white text-sm font-semibold rounded-sm hover:bg-[#17205E]" data-testid="quote-dim-add">Add item</button>
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-slate-600 hover:text-[#1E2A78]">Cancel</button>
      </div>
    </div>
  );
}

function ItemRow({ item, line, cur, onQty, onRemove }) {
  return (
    <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 py-3 border-t border-slate-100" data-testid={`quote-item-${item.key}`}>
      <Package className="h-5 w-5 text-[#1E2A78] shrink-0" />
      <div className="flex-1 min-w-[60%] sm:min-w-0">
        <div className="text-sm font-semibold text-[#0F172A]">{LABELS[item.type]}</div>
        <div className="text-xs text-slate-500 truncate">
          {item.length ? `${item.length}×${item.width}×${item.height} in` : "Fixed price"}
          {line?.cubic_ft ? ` · ${line.cubic_ft} cu ft` : ""}{item.description ? ` · ${item.description}` : ""}
        </div>
      </div>
      <div className="flex items-center border border-slate-200 rounded-sm">
        <button type="button" onClick={() => onQty(item.qty - 1)} className="p-1.5 hover:bg-slate-100" data-testid={`quote-item-dec-${item.key}`}><Minus className="h-3.5 w-3.5" /></button>
        <span className="w-8 text-center text-sm font-mono" data-testid={`quote-item-qty-${item.key}`}>{item.qty}</span>
        <button type="button" onClick={() => onQty(item.qty + 1)} className="p-1.5 hover:bg-slate-100" data-testid={`quote-item-inc-${item.key}`}><Plus className="h-3.5 w-3.5" /></button>
      </div>
      <div className="ml-auto sm:ml-0 w-24 text-right text-sm font-mono font-semibold">{line ? fmtMoney(line.total, cur) : "—"}</div>
      <button type="button" onClick={onRemove} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`quote-item-remove-${item.key}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

export function ItemPicker({ items, setItems, rate, currency, lines }) {
  const [sizing, setSizing] = useState(null);
  const pick = (t) => {
    if (t.fixed) setItems([...items, { key: newKey(), type: t.type, qty: 1 }]);
    else setSizing(t.type);
  };
  const setQty = (key, qty) => setItems(items.map((i) => (i.key === key ? { ...i, qty: Math.max(1, qty) } : i)));
  return (
    <Step n="2" title="What are you shipping?" hint="Barrels are a flat price. Everything else is priced by size. Add as many items as you need." testId="quote-step-items">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {TYPES.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.type} type="button" disabled={!rate} onClick={() => pick(t)} data-testid={`quote-add-${t.type}`}
              className="text-left p-4 border border-slate-200 rounded-sm hover:border-[#1E2A78] hover:bg-[#1E2A78]/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed group">
              <Icon className="h-6 w-6 text-[#1E2A78] mb-2 group-hover:scale-110 transition-transform" />
              <div className="text-sm font-semibold text-[#0F172A]">{t.label}</div>
              {t.sub && <div className="text-[11px] text-slate-500">{t.sub}</div>}
              <div className="mt-2 text-xs font-mono text-[#1E2A78] font-semibold">{priceHint(t, rate, currency)}</div>
            </button>
          );
        })}
      </div>
      {sizing && <DimensionForm type={sizing} onCancel={() => setSizing(null)} onAdd={(it) => { setItems([...items, it]); setSizing(null); }} />}
      <div className="mt-5" data-testid="quote-items-list">
        {items.length === 0 ? <p className="text-sm text-slate-400">No items yet. {rate ? "Tap an item type above to add it." : "Choose your route first."}</p>
          : items.map((it, idx) => <ItemRow key={it.key} item={it} line={lines?.length === items.length ? lines[idx] : null} cur={currency} onQty={(q) => setQty(it.key, q)} onRemove={() => setItems(items.filter((x) => x.key !== it.key))} />)}
      </div>
    </Step>
  );
}
